/**
 * Authentication context
 * Provides authentication state and methods throughout the app
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  AuthContextValue,
  AuthState,
  LoginCredentials,
  User,
} from "@/types/auth";
import { authService, hasStoredTokens } from "@/services/authService";
import { HttpError } from "@/utils/httpClient";

/**
 * Default authentication state
 */
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // Start loading to check for stored session
  error: null,
};

/**
 * Authentication context
 */
const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Authentication provider props
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication provider component
 */
export function AuthProvider({ children }: AuthProviderProps): React.ReactElement {
  const [state, setState] = useState<AuthState>(initialState);

  /**
   * Set user and authenticated state
   */
  const setUser = useCallback((user: User | null) => {
    setState({
      user,
      isAuthenticated: user !== null,
      isLoading: false,
      error: null,
    });
  }, []);

  /**
   * Set error state
   */
  const setError = useCallback((error: string) => {
    setState((prev) => ({
      ...prev,
      isLoading: false,
      error,
    }));
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: null,
    }));
  }, []);

  /**
   * Login with credentials
   */
  const login = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await authService.login(credentials);
        setUser(response.user);
      } catch (error) {
        const message =
          error instanceof HttpError && error.statusCode === 401
            ? "Credenciales inválidas"
            : error instanceof HttpError
              ? error.message
              : "Error al iniciar sesión. Por favor, inténtalo de nuevo.";
        setError(message);
        throw error;
      }
    },
    [setUser, setError],
  );

  /**
   * Logout current user
   */
  const logout = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      await authService.logout();
    } finally {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  }, []);

  /**
   * Check for existing session on mount
   */
  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      if (!hasStoredTokens()) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const user = await authService.getCurrentUser();
        setUser(user);
      } catch {
        // Token invalid or expired, clear and show login
        authService.clearAuth();
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    void checkAuth();
  }, [setUser]);

  /**
   * Memoized context value
   */
  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
      clearError,
    }),
    [state, login, logout, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access authentication context
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

export default AuthContext;
